import React, { useEffect, useState } from 'react';
import Paper from '@material-ui/core/Paper';
import { withStyles } from '@material-ui/core';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { TableSelection } from '@devexpress/dx-react-grid-material-ui';
import { fetchRooms, createTemplateRooms } from '../../../thunks/Rooms';
import { actions } from '../../../reducers/Rooms';
import {
  templateRoomsColumns,
  editableTemplateRoomsColumns,
  NEW_TITLE,
} from '../../../utils/ListViewUtil';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import { Table, Cell } from '@tribiahq/interaxo-react-components';
import useEnvironmentPreferences from '../../../effects/useEnvironmentPreferences';
import useDebounced from '../../../effects/useDebounced';
import useWorkspaces from '../../../effects/useWorkspaces';

import { CellEditor } from '../../../component/CellEditor';
import useCreateTemplateRooms from '../../../effects/useCreateTemplateRooms';
import useRooms from '../../../effects/useRooms';
import LinearProgress from '@material-ui/core/LinearProgress';
import PropTypes from 'prop-types';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import RefreshIcon from '@material-ui/icons/Refresh';

const styles = theme => ({
  root: {
    margin: theme.spacing(3),
    padding: theme.spacing(3),
    display: 'flex',
    flexWrap: 'wrap',
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: '40%',
  },
  buttonContainer: {
    marginLeft: '5%',
    marginRight: theme.spacing(1),
    marginTop: 26,
    width: '10%',
  },
  form: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
  },
  refreshButtonContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  refreshButton: {
    marginTop: theme.spacing(0.5),
  },
});

function RoomsContainer({ classes }) {
  const { handleSubmit, setValue, errors, control } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      'from-value': '',
      'to-value': '',
    },
  });
  const [communityId, setCommunityId] = useState('');
  const [targetCommunityId, setTargetCommunityId] = useState('');
  const [workspaceFetchCommunityId, setWorkspaceFetchCommunityId] = useState('');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  const [selected, setSelected] = useState([]);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const dispatch = useDispatch();
  const rooms = useRooms();
  const { workspaces, isLoading: isLoadingWorkspaces } = useWorkspaces(workspaceFetchCommunityId);
  const templateRooms = rooms.rooms.filter(room => room.template);
  const createTemplateRoomsStatus = useCreateTemplateRooms();
  const environmentPreferences = useEnvironmentPreferences();

  useEffect(() => {
    if (
      !selectedWorkspaceId &&
      !isLoadingWorkspaces &&
      workspaceFetchCommunityId &&
      workspaces.length === 1
    ) {
      setSelectedWorkspaceId(workspaces[0].nodeId);
    }
  }, [
    workspaces,
    isLoadingWorkspaces,
    selectedWorkspaceId,
    workspaceFetchCommunityId,
  ]);

  useEffect(() => {
    if (
      environmentPreferences.isRequested &&
      !environmentPreferences.isLoading &&
      environmentPreferences.preferences
    ) {
      const favoriteTemplateCommunity =
        environmentPreferences.preferences.favoriteTemplateCommunityId;
      setValue('from-value', favoriteTemplateCommunity);
      setCommunityId(favoriteTemplateCommunity);
    }
  }, [
    setValue,
    setCommunityId,
    environmentPreferences.isRequested,
    environmentPreferences.isLoading,
    environmentPreferences.preferences,
  ]);

  useDebounced({
    delayedFunction: async () => {
      const needToReFetchRooms =
        (!rooms.isRequested || rooms.isRequested.communityId !== communityId) &&
        !rooms.isLoading;
      const invalid = !!errors['from-value'];
      const allowedToFetch = !!(communityId && communityId.length >= 5);

      if (invalid || !allowedToFetch) {
        return;
      }
      if (needToReFetchRooms) {
        setSelected([]);
        setSelectedRooms([]);
        dispatch(actions.clearRooms());
        dispatch(fetchRooms({ communityId }));
      }
    },
    delay: 500,
    dependencies: [
      rooms.isLoading,
      rooms.isRequested,
      communityId,
      errors,
      dispatch,
      setSelected,
      setSelectedRooms,
    ],
  });

  const refreshRooms = () => {
    if (!!errors['from-value'] || !communityId) return;

    setSelected([]);
    setSelectedRooms([]);
    dispatch(actions.clearRooms());
    dispatch(fetchRooms({ communityId }));
  };

  useDebounced({
    delayedFunction: async () => {
      const invalid = !!errors['to-value'];
      const allowedToFetch = !!(targetCommunityId && targetCommunityId.length >= 5);

      if (invalid || !allowedToFetch) return;

      if (workspaceFetchCommunityId !== targetCommunityId) {
        setSelectedWorkspaceId('');
        setWorkspaceFetchCommunityId(targetCommunityId);
      }
    },
    delay: 500,
    dependencies: [
      targetCommunityId,
      errors,
      workspaceFetchCommunityId,
    ],
  });

  const loadWorkspaces = () => {
    if (!!errors['to-value'] || !targetCommunityId) return;

    setSelectedWorkspaceId('');
    setWorkspaceFetchCommunityId(targetCommunityId);
  };

  const convertRoomIntoModel = room => ({
    templateTenant: communityId,
    templateNodeId: room.nodeId,
    shortName: room.shortName,
    title: room.title,
    newTitle: room.title,
    template: true,
    withContent: false,
    description: room.description,
    workspace: room.workspace,
  });

  const convertModelIntoDTO = model => ({
    templateTenant: model.templateTenant,
    templateNodeId: model.templateNodeId,
    shortName: model.shortName,
    title: model.newTitle,
    description: model.description,
    visibility: 'PUBLIC',
    sitePreset: model.template ? 'site-templates' : 'site-dashboard',
    workspaceId: model.workspace,
    withContent: model.withContent,
  });

  const onSubmit = params => {
    if (!selectedWorkspaceId) return;

    const toCommunityId = params['to-value'];
    const templateRoomsDTOs = selectedRooms.map(room => ({
      ...convertModelIntoDTO(room),
      workspaceId: selectedWorkspaceId,
    }));
    dispatch(
      createTemplateRooms({
        communityId: toCommunityId,
        templateRooms: templateRoomsDTOs,
      }),
    );
  };

  const handleFromChange = communityId => {
    if (!errors['from-value']) {
      setCommunityId(communityId);
    }
  };

  if (!environmentPreferences.isRequested || environmentPreferences.isLoading) {
    return <LinearProgress />;
  }

  const generateShortName = title =>
    title
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/(?!-)[\W_]/g, '');

  const validationRules = {
    newTitle: [
      {
        isValid: value => value?.trim().length > 0,
        errorText: 'The field is required',
      },
      {
        isValid: value => !/[/?*:"><|\\]/.test(value),
        errorText:
          'The field cannot contain the following characters: / \\\\ ? * : " > < |',
      },
      {
        isValid: value => !/(^[\s*])/i.test(value),
        errorText: 'The field cannot start with a space',
      },
    ],
  };

  const handleSelectionChange = selection => {
    setSelected(selection);

    if (selection.length === 0) {
      setSelectedRooms([]);
      return;
    }

    let savedSelected = selectedRooms.filter(room =>
      selection.includes(room.templateNodeId),
    );
    let newlySelected = selection
      .filter(
        nodeId => !savedSelected.some(room => room.templateNodeId === nodeId),
      )
      .map(nodeId => templateRooms.find(room => room.nodeId === nodeId))
      .map(room => convertRoomIntoModel(room));

    setSelectedRooms(savedSelected.concat(newlySelected));
  };

  const handleCellChange = (row, columnName, newValue) => {
    const newModel = row;
    newModel[columnName] = newValue;

    if (columnName === NEW_TITLE) {
      newModel.shortName = generateShortName(newValue);
    }

    const newSelectedRooms = selectedRooms.map(room =>
      room.templateNodeId === newModel.templateNodeId ? newModel : room,
    );

    setSelectedRooms(newSelectedRooms);
  };

  const getCellComponentRenderer = props => (
    <Cell className={classes.cell} {...props}>
      <CellEditor
        record={props.row}
        fieldName={props.column.name}
        onValueChange={newValue =>
          handleCellChange(props.row, props.column.name, newValue)
        }
        disabled={props.column.readOnly}
        type={props.column.type}
        validators={validationRules[props.column.name]}
      />
    </Cell>
  );

  return (
    <Paper className={classes.root}>
      {createTemplateRoomsStatus.isLoading && <LinearProgress />}
      <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
        <Controller
          control={control}
          name="from-value"
          rules={{
            required: 'Required',
            pattern: {
              value: /^[A-Za-z0-9._-]+$/i,
              message: 'Invalid communityId',
            },
          }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <TextField
              id="from-value"
              className={classes.textField}
              label="From"
              placeholder="Community ID"
              margin="normal"
              variant="outlined"
              onChange={e => {
                handleFromChange(e.target.value);
                onChange(e.target.value);
              }}
              error={!!errors['from-value']}
              helperText={errors['from-value'] && errors['from-value'].message}
              inputRef={ref}
              value={value}
            />
          )}
        />
        <div className={classes.refreshButtonContainer}>
          <Tooltip title="Refresh rooms from this community">
            <IconButton
              onClick={refreshRooms}
              disabled={!!errors['from-value'] || !communityId}
              size="small"
              color="primary"
              aria-label="refresh rooms"
              type="button"
              className={classes.refreshButton}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        <Controller
          control={control}
          name="to-value"
          rules={{
            required: 'Required',
            pattern: {
              value: /^[A-Za-z0-9._-]+$/i,
              message: 'Invalid communityId',
            },
          }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <TextField
              id="to-value"
              className={classes.textField}
              label="To"
              placeholder="Community ID"
              margin="normal"
              variant="outlined"
              onChange={e => {
                setTargetCommunityId(e.target.value);
                setSelectedWorkspaceId('');
                setWorkspaceFetchCommunityId('');
                onChange(e.target.value);
              }}
              error={!!errors['to-value']}
              helperText={errors['to-value'] && errors['to-value'].message}
              inputRef={ref}
              value={value}
            />
          )}
        />
        <FormControl
          variant="outlined"
          className={classes.textField}
          margin="normal"
          error={!selectedWorkspaceId && !!workspaceFetchCommunityId && !isLoadingWorkspaces}
          required>
          <InputLabel id="workspace-label">Workspace</InputLabel>
          <Select
            labelId="workspace-label"
            id="workspace-select"
            value={selectedWorkspaceId}
            onChange={e => setSelectedWorkspaceId(e.target.value)}
            label="Workspace"
            disabled={!workspaceFetchCommunityId || isLoadingWorkspaces}>
            {workspaces.map(ws => (
              <MenuItem key={ws.nodeId} value={ws.nodeId}>
                {ws.title}
              </MenuItem>
            ))}
          </Select>
          {!selectedWorkspaceId && workspaceFetchCommunityId && !isLoadingWorkspaces && (
            <FormHelperText>Required</FormHelperText>
          )}
        </FormControl>
        <div className={classes.refreshButtonContainer}>
          <Tooltip title="Load workspaces for this community">
            <IconButton
              onClick={loadWorkspaces}
              disabled={!!errors['to-value'] || !targetCommunityId || isLoadingWorkspaces}
              size="small"
              color="primary"
              aria-label="load workspaces"
              type="button"
              className={classes.refreshButton}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        <div className={classes.buttonContainer}>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={
              !!(errors['from-value'] || errors['to-value']) ||
              !selectedWorkspaceId ||
              createTemplateRoomsStatus.isLoading
            }>
            Go
          </Button>
        </div>
      </form>
      <Table
        columns={templateRoomsColumns}
        rows={templateRooms}
        virtual
        height={300}
        gridConfig={{
          getRowId: row => row.nodeId,
        }}
        columnResizing={{
          defaultColumnWidths: templateRoomsColumns,
        }}
        search={{
          panelConfig: {
            messages: {
              searchPlaceholder: 'Search...',
            },
          },
        }}
        sorting={{
          stateConfig: {
            defaultSorting: [
              { columnName: 'template', direction: 'desc' },
              { columnName: 'title', direction: 'asc' },
            ],
          },
        }}
        selection={{
          stateConfig: {
            selection: selected,
            onSelectionChange: handleSelectionChange,
          },
          config: {
            showSelectAll: true,
            headerCellComponent: TableSelection.HeaderCell,
            cellComponent: TableSelection.Cell,
            selectByRowClick: true,
            highlightRow: true,
            rowAlternationEnabled: true,
          },
        }}
        tableConfig={{
          cellComponent: props => {
            if (props.column.customComponent) {
              return (
                <Cell {...props}>
                  {props.column.customComponent(props.row)}
                </Cell>
              );
            }
            return <Cell {...props} />;
          },
        }}
      />

      <Table
        columns={editableTemplateRoomsColumns}
        rows={selectedRooms.map(item => ({ ...item }))}
        virtual
        height={300}
        gridConfig={{
          getRowId: row => row.templateNodeId,
        }}
        columnResizing={{
          defaultColumnWidths: editableTemplateRoomsColumns,
        }}
        search={{
          panelConfig: {
            messages: {
              searchPlaceholder: 'Search...',
            },
          },
        }}
        sorting={{
          stateConfig: {
            defaultSorting: [{ columnName: 'title', direction: 'asc' }],
          },
        }}
        tableConfig={{
          cellComponent: getCellComponentRenderer,
          estimatedRowHeight: 49,
        }}
      />
    </Paper>
  );
}

RoomsContainer.propTypes = {
  classes: PropTypes.shape({
    root: PropTypes.string,
    textField: PropTypes.string,
    buttonContainer: PropTypes.string,
    cell: PropTypes.string,
    form: PropTypes.string,
    refreshButtonContainer: PropTypes.string,
    refreshButton: PropTypes.string,
  }),
};

RoomsContainer.defaultProps = {
  classes: {},
};

export default withStyles(styles)(RoomsContainer);
